import { faDownload, faEye, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Image, ImageProps } from '@mantine/core';
import { PopConfirm } from '../PopConfirm';
import { useTranslation } from 'react-i18next';
import { VActionIcon } from '../VActionIcon';
import { ImagePreviewModal } from '../ImagePreviewModal';
import classes from './VImage.module.css';
import clsx from 'clsx';
import React, { useState } from 'react';

export interface VImageProps extends ImageProps {
  onDelete?: () => void;
  alt?: string;
  onClick?: React.MouseEventHandler<HTMLImageElement>;
  width?: number | string;
  height?: number | string;
  enableActions?: boolean;
  deletePopConfirm: {
    title: string;
    description: string;
    btnConfirmLabel: string;
    btnCancelLabel: string;
  };
}

export const VImage = React.forwardRef<HTMLDivElement, VImageProps>(
  (
    { src, alt, onDelete, onClick, className, enableActions, deletePopConfirm, width, height, ...rest }: VImageProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const { t } = useTranslation(['translation', 'page-planner']);
    const [showModal, setShowModal] = useState(false);
    const [hovering, setHovering] = useState(false);
    const [showDeletePopConfirm, setShowDeletePopConfirm] = useState(false);

    const handleViewClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowModal(true);
    };

    const handleDownloadClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (src) {
        const link = document.createElement('a');
        link.href = src.toString();
        link.target = '_blank';
        link.download = alt || 'image';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowDeletePopConfirm(true);
    };

    const handleDeleteConfirm = () => {
      onDelete?.();
    };

    const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <>
        <div
          className={`${classes.imageContainer} ${className || ''}`}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          ref={ref}
          style={{ width, height }}
        >
          <Image
            src={src}
            alt={alt}
            onClick={handleImageClick}
            className={`${classes.image} ${hovering && enableActions ? classes.hovering : ''}`}
            width="100%"
            height="100%"
            {...rest}
          />
          {(hovering || showDeletePopConfirm) && enableActions && (
            <div className={classes.actionButtons}>
              <VActionIcon
                className={clsx(classes.actionButton, classes.downloadButton)}
                onClick={handleDownloadClick}
                tooltipLabel={t('download')}
                tooltipOptions={{
                  position: 'bottom',
                }}
                radius="xl"
                size={36}
              >
                <FontAwesomeIcon icon={faDownload} />
              </VActionIcon>
              <VActionIcon
                className={clsx(classes.actionButton, classes.viewButton)}
                onClick={handleViewClick}
                tooltipLabel={t('view')}
                tooltipOptions={{
                  position: 'bottom',
                }}
                radius="xl"
                size={36}
              >
                <FontAwesomeIcon icon={faEye} />
              </VActionIcon>

              {onDelete && (
                <PopConfirm
                  title={deletePopConfirm?.title}
                  description={deletePopConfirm?.description}
                  btnConfirmLabel={deletePopConfirm?.btnConfirmLabel}
                  btnCancelLabel={deletePopConfirm?.btnCancelLabel}
                  onConfirm={handleDeleteConfirm}
                  onCancel={() => setShowDeletePopConfirm(false)}
                  opened={showDeletePopConfirm}
                  withArrow={true}
                >
                  <VActionIcon
                    className={clsx(classes.actionButton, classes.deleteButton)}
                    onClick={handleDeleteClick}
                    tooltipLabel={t('delete')}
                    tooltipOptions={{
                      position: 'bottom',
                    }}
                    radius="xl"
                    size={36}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </VActionIcon>
                </PopConfirm>
              )}
            </div>
          )}
        </div>

        <ImagePreviewModal opened={showModal} onClose={() => setShowModal(false)} src={src?.toString()} alt={alt} />
      </>
    );
  },
);
